import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownLeft, BellRinging, CalendarBlank, CheckCircle, Circle, CircleHalf, CurrencyEur, Drop, FileText, Flame, House, PencilSimple, Percent,
  Plus, Receipt, Ruler, Scales, TrendUp, Vault, Wallet, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import {
  Chip, FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, PanelZeile, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT, BUCHHALTUNG_FOLGT_LANG } from '../../../../../lib/immobilien/hinweise';
import { kautionInfo, mietentwicklung } from '../../../../../lib/immobilien/einheitDetail';
import { historieUpdate, leseZahl, neueAenderung, startAusMieter } from '../../../../../lib/immobilien/mietverhaeltnisFormular';
import { aktuellerStand, type Mietstand } from '../../../../../lib/mieter/mietstaende';
import { buchungsZeilen, eingangBalken, kautionHoehe, kautionsRaten, kontoKennzahlen, zahlungFehler, zahlungVorschlag } from '../../../../../lib/mieter/finanzen';
import { bucheZahlung } from '../../../../../lib/mietkonto/api';
import { meldeMietkontoGeaendert } from '../../../../../lib/mietkonto/useMietkonto';
import { mieterKonto } from '../../../../../lib/mietkonto/typen';
import { meldeMieterGeaendert } from '../../../../../lib/mieter/useMieterDetail';
import { supabase } from '../../../../../lib/supabase/client';
import { MietstandEditor } from '../../../objekte/einheit/bereiche/MietstandEditor';
import { DatenZeile, ibanText } from '../../../objekte/einheit/bereiche/teile';
import { useMieterSeite } from '../MieterSeitenKontext';

const PIP: Record<string, string> = { ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--bad)', vor: 'var(--fill2)' };

/** Knopf ohne Datenquelle: sichtbar, aber gesperrt mit Hinweis (README §8: kein toter Button). */
function FolgtKnopf({ icon, children, hinweis }: { icon: Icon; children: React.ReactNode; hinweis: string }) {
  return <ImmoButton icon={icon} disabled title={hinweis} aria-label={`${String(children)} – ${hinweis}`}>{children}</ImmoButton>;
}

// ── Mietkonto (Screens 20–22, 45) ──────────────────────────────────────────────
export function KontoPanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const [toast, zeige] = useToastPill();
  const k = s.konto;
  const [betrag, setBetrag] = useState(() => (k ? String(zahlungVorschlag(k)).replace('.', ',') : ''));
  const [datum, setDatum] = useState(s.heute);
  const [laeuft, setLaeuft] = useState(false);
  const bereit = s.mietkonto === 'ok' && !!m.objekt_id && !!m.einheit_id;
  // Mietkonto lädt nach dem Öffnen: Betrag dann einmal vorbelegen (Rückstand, sonst Monatsmiete)
  useEffect(() => {
    if (k) setBetrag(alt => alt || String(zahlungVorschlag(k)).replace('.', ','));
  }, [k]);

  const buche = async () => {
    const f = zahlungFehler(betrag, datum, s.heute);
    if (f) { zeige(f, true); return; }
    if (!m.objekt_id || !m.einheit_id) return;
    setLaeuft(true);
    try {
      const b = leseZahl(betrag) ?? 0;
      await bucheZahlung({
        objektId: m.objekt_id, kontoSchluessel: mieterKonto(m.id), einheitId: m.einheit_id, mieterId: m.id,
        zahlerName: s.name, datum, betrag: b, quelle: 'mietkonto',
      });
      meldeMietkontoGeaendert(m.objekt_id);
      zeige(`Zahlung über ${eur(b)} gebucht`);
    } catch (e) {
      zeige(`Nicht gebucht: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  const kz = k ? kontoKennzahlen(k) : null;
  const zeilen = k ? buchungsZeilen(k) : [];
  const balken = k ? eingangBalken(k) : [];

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Saldo" icon={Scales} ton={kz && kz.saldo < -0.5 ? 'bad' : kz ? 'ok' : 'neutral'} wertFarbig={!!kz && kz.saldo < -0.5}
          wert={kz ? eur(kz.saldo) : '–'} />
        <KpiTile layout="kachel" label="Soll 12 Monate" icon={CalendarBlank} ton="neutral" wert={kz ? eur(kz.soll12, { nachkomma: 0 }) : '–'} />
        <KpiTile layout="kachel" label="Eingegangen" icon={ArrowDownLeft} ton="ok" wert={kz ? eur(kz.ist12, { nachkomma: 0 }) : '–'} />
      </KpiReihe>

      {s.mietkonto !== 'ok' && (
        <LeerZustand icon={Wallet} titel={s.mietkonto === 'ohne' ? 'Keinem Objekt zugeordnet' : s.mietkonto === 'fehler' ? 'Mietkonto nicht geladen' : BUCHHALTUNG_FOLGT}
          text={s.mietkonto === 'ohne' ? 'Ohne Objekt und Einheit gibt es kein Mietkonto.' : BUCHHALTUNG_FOLGT_LANG} />
      )}

      {k && (
        <>
          <InnenKarte style={{ background: 'var(--glass)' }}>
            <KartenTitel>Zahlungseingang je Monat</KartenTitel>
            <div className="flex items-end" style={{ gap: 8, height: 130 }}>
              {balken.map(b => (
                <div key={b.monat} className="flex flex-1 flex-col items-center justify-end h-full" style={{ gap: 6 }}>
                  <div title={b.titel} style={{ width: '100%', maxWidth: 38, height: `${b.hoehe}%`, borderRadius: 9, background: PIP[b.farbe] }} />
                  <span style={{ fontSize: 10, color: 'var(--ink3)' }}>{b.kurz}</span>
                </div>
              ))}
            </div>
          </InnenKarte>
          <InnenKarte style={{ background: 'var(--glass)' }}>
            <KartenTitel>Buchungen</KartenTitel>
            {zeilen.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Buchungen</span>}
            <div className="flex flex-col">
              {zeilen.map(z => (
                <PanelZeile key={z.key} icon={z.ton === 'bad' ? WarningCircle : z.ton === 'info' ? ArrowDownLeft : CheckCircle} ton={z.ton}
                  titel={z.titel} sub={z.sub} rechts={z.rechts} rechtsFarbe={`var(--${z.ton})`} />
              ))}
            </div>
          </InnenKarte>
        </>
      )}
      {s.mietkonto === 'ok' && !k && (
        <LeerZustand icon={Receipt} titel="Noch keine Buchungen" text="Für dieses Mietverhältnis gibt es im Mietkonto noch kein Soll und keine Zahlung." />
      )}

      <InnenKarte style={{ background: 'var(--glass)' }}>
        <KartenTitel>Zahlung erfassen</KartenTitel>
        <FeldRaster minBreite={200}>
          <TextFeld label="Betrag" icon={CurrencyEur} suffix="€" inputMode="decimal" wert={betrag} onChange={setBetrag} disabled={!bereit} placeholder="0,00" />
          <TextFeld label="Eingang am" icon={CalendarBlank} type="date" wert={datum} onChange={setDatum} disabled={!bereit} max={s.heute} />
        </FeldRaster>
        {!bereit && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{s.mietkonto === 'ohne' || !m.einheit_id ? 'Zahlungen brauchen eine zugeordnete Einheit.' : 'Zahlungen lassen sich buchen, sobald die Buchhaltung angebunden ist.'}</span>}
        <div className="flex flex-wrap justify-end" style={{ gap: 8 }}>
          <FolgtKnopf icon={BellRinging} hinweis="Mahnwesen startet mit der Buchhaltung">Zahlungserinnerung senden</FolgtKnopf>
          <ImmoButton art="akzent" groesse={44} icon={Plus} disabled={!bereit || laeuft} onClick={() => { void buche(); }}>
            {laeuft ? 'Wird gebucht …' : 'Zahlung buchen'}
          </ImmoButton>
        </div>
      </InnenKarte>
    </>
  );
}

// ── Gesamtmiete / Miete anpassen (Screen 23) ───────────────────────────────────
export function MietePanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const [editorRunde, setEditorRunde] = useState(0);
  const start = useMemo(() => startAusMieter(m, s.heute), [m, s.heute]);
  const akt = aktuellerStand(start.staende, s.heute);
  const vorlage = useMemo(() => neueAenderung(akt, start.entwurf, Date.now() + editorRunde), [akt, start.entwurf, editorRunde]);
  // inkl. Anfangsstand ab Mietbeginn – so, wie es beim Speichern geschrieben wird
  const entwicklung = mietentwicklung(start.staende);

  // Mietanpassung = neuer Mietstand mit „gültig ab“; der bisherige bleibt erhalten (lib/mieter/mietstaende)
  const speichere = async (neu: Mietstand) => {
    setLaeuft(true);
    const u = historieUpdate([...start.staende, neu], s.heute);
    const { error } = await supabase.from('mieter').update({ miet_historie: u.miet_historie, ...(u.spiegel ?? {}) }).eq('id', m.id);
    setLaeuft(false);
    if (error) { zeige(`Nicht gespeichert: ${error.message}`, true); return; }
    zeige(`Neue Miete ab ${deDatum(neu.gueltigAb)} gespeichert`);
    setEditorRunde(r => r + 1);
    if (m.objekt_id) meldeMietkontoGeaendert(m.objekt_id);
    meldeMieterGeaendert(m.id);
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Gesamtmiete" icon={CurrencyEur} ton="acc" wert={eur(s.miete.gesamt)} />
        <KpiTile layout="kachel" label="Kaltmiete" icon={House} ton="acc" wert={eur(s.miete.kalt)} />
        <KpiTile layout="kachel" label="Nebenkosten" icon={Drop} ton="info" wert={eur(s.miete.nk)} />
        <KpiTile layout="kachel" label="Heizkosten" icon={Flame} ton="bad" wert={eur(s.miete.hk)} />
        {s.miete.weitere > 0 && <KpiTile layout="kachel" label="Weitere Kosten" icon={Plus} ton="warn" wert={eur(s.miete.weitere)} />}
      </KpiReihe>

      <InnenKarte style={{ background: 'var(--glass)' }}>
        <KartenTitel>Mietentwicklung</KartenTitel>
        {entwicklung.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Mietstände – die Miete steht direkt im Mietverhältnis.</span>}
        <div className="flex flex-col">
          {[...entwicklung].reverse().map((st, i, liste) => {
            const zukunft = st.ab > s.heute;
            const titel = zukunft ? 'geplant' : i === liste.findIndex(x => x.ab <= s.heute) ? 'aktuell' : i === liste.length - 1 ? 'Mietbeginn' : 'Mietänderung';
            return (
              <PanelZeile key={`${st.ab}-${i}`} icon={TrendUp} ton={zukunft ? 'info' : 'acc'} titel={titel} sub={`ab ${deDatum(st.ab)} · gesamt ${eur(st.gesamt)}`}
                rechts={`${eur(st.kalt, { nachkomma: 0 })} kalt`} />
            );
          })}
        </div>
      </InnenKarte>

      <MietstandEditor key={editorRunde} titel="Miete anpassen" uebernehmenLabel={laeuft ? 'Wird gespeichert …' : 'Speichern'} laeuft={laeuft}
        stand={vorlage} istNeu andereStaende={start.staende}
        onAbbrechen={() => setEditorRunde(r => r + 1)} onUebernehmen={st => { void speichere(st); }} />
    </>
  );
}

// ── Kaution (Screen 25) ────────────────────────────────────────────────────────
export function KautionPanel() {
  const s = useMieterSeite();
  const navigate = useNavigate();
  const m = s.daten.mieter;
  const k = kautionInfo(m);
  const raten = kautionsRaten(k.soll, k.ist, m.kaution_raten);
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const mvPfad = m.objekt_id && m.einheit_id
    ? `/admin/objekte/${encodeURIComponent(m.objekt_id)}/einheiten/${encodeURIComponent(m.einheit_id)}?bereich=mvedit&mieter=${encodeURIComponent(m.id)}&reiter=miete`
    : null;

  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" label="Kaution" icon={Vault} ton="acc" wert={k.soll > 0 ? eur(k.soll, { nachkomma: 0 }) : '–'} />
        <KpiTile layout="kachel" label="Zinsen" icon={Percent} ton="ok" wert="–" sub="nicht erfasst" />
        <KpiTile layout="kachel" label="Höhe" icon={Ruler} ton="info" wert={kautionHoehe(k.soll, s.miete.kalt)} />
      </KpiReihe>

      <InnenKarte style={{ background: 'var(--glass)' }}>
        <KartenTitel rechts={k.soll > 0 ? `eingegangen ${eur(k.ist)}` : undefined}>Ratenzahlung</KartenTitel>
        {raten.length === 0 ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Kaution hinterlegt.</span> : (
          <div className="flex flex-col">
            {raten.map(r => (
              <PanelZeile key={r.nr} icon={r.status === 'eingegangen' ? CheckCircle : r.status === 'teilweise' ? CircleHalf : Circle}
                ton={r.status === 'eingegangen' ? 'ok' : r.status === 'teilweise' ? 'warn' : 'neutral'}
                titel={`${r.nr}. Rate`} sub={r.status === 'eingegangen' ? 'eingegangen' : r.status === 'teilweise' ? `teilweise eingegangen · ${r.faellig}` : r.faellig}
                rechts={eur(r.betrag, { nachkomma: 0 })} />
            ))}
          </div>
        )}
      </InnenKarte>

      <InnenKarte style={{ background: 'var(--glass)' }}>
        <KartenTitel rechts={k.status !== 'keine' ? <Chip groesse="xs" ton={k.status === 'hinterlegt' ? 'ok' : 'warn'}>{k.status === 'hinterlegt' ? 'vollständig' : k.status === 'teilweise' ? 'teilweise' : 'ausstehend'}</Chip> : undefined}>
          Details
        </KartenTitel>
        <div className="flex flex-col">
          <DatenZeile label="Art" wert={text(m.kaution_art) || '–'} />
          {text(m.kaution_gegenstand) && <DatenZeile label="Gegenstand" wert={text(m.kaution_gegenstand)} />}
          <DatenZeile label="Kautionskonto" wert={text(m.kaution_iban) ? ibanText(text(m.kaution_iban)) : '–'} zahl />
          {(text(m.kaution_gueltig_ab) || text(m.kaution_gueltig_bis)) && (
            <DatenZeile label="Gültig" wert={`${deDatum(text(m.kaution_gueltig_ab) || null)}${text(m.kaution_gueltig_bis) ? ` bis ${deDatum(text(m.kaution_gueltig_bis))}` : ''}`} zahl />
          )}
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <FolgtKnopf icon={FileText} hinweis="Wird noch angebunden">Kautionsbescheinigung</FolgtKnopf>
          <FolgtKnopf icon={Receipt} hinweis="Wird noch angebunden">Kaution abrechnen</FolgtKnopf>
          {mvPfad && <ImmoButton icon={PencilSimple} onClick={() => navigate(mvPfad)}>Im Mietverhältnis ändern</ImmoButton>}
        </div>
      </InnenKarte>
    </>
  );
}
